import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Subscription } from 'rxjs';
import { StatisticsActions } from '../+state/statistics.actions';
import { statisticsFeature } from '../+state/statistics.reducer';

import * as StatisticsSelectors from '../+state/statistics.selectors';
import { Period } from '../entities/chart-and-stats.entity';
import { Filter } from '../entities/trade.entity';
import { WeightingType } from '../entities/weighting.entity';

@Injectable({ providedIn: 'root' })
export class StatisticsFacade {
  private readonly _store: Store = inject(Store);
  private readonly _subscriptions: Subscription[] = [];

  public tradeTicker$ = this._store.select(StatisticsSelectors.selectTradeTicker);
  public error$ = this._store.select(statisticsFeature.selectError);
  public tradeTickerLoading$ = this._store.select(statisticsFeature.selectTradeTickerLoading);
  public tradeTickerLoaded$ = this._store.select(statisticsFeature.selectTradeTickerInitiallyLoaded);
  public tradeTickerFilter$ = this._store.select(statisticsFeature.selectTradeTickerFilter);
  public myTrades$ = this._store.select(statisticsFeature.selectMyTrades);
  public myTradesLoading$ = this._store.select(statisticsFeature.selectMyTradesLoading);
  public weighting$ = this._store.select(StatisticsSelectors.selectWeighting);
  public weightingLoading$ = this._store.select(StatisticsSelectors.selectWeightingLoading);
  public weightingType$ = this._store.select(statisticsFeature.selectWeightingType);
  public myNews$ = this._store.select(statisticsFeature.selectNews);
  public myNewsLoading$ = this._store.select(statisticsFeature.selectNewsLoading);
  public myChartWithCash$ = this._store.select(StatisticsSelectors.selectMyChartWithCash);
  public myChartWithoutCash$ = this._store.select(StatisticsSelectors.selectMyChartWithoutCash);
  public myChartDepotWithCash$ = this._store.select(StatisticsSelectors.selectMyChartDepotWithCash);
  public myStats$ = this._store.select(StatisticsSelectors.selectStats);
  public myChartAndStatsLoading$ = this._store.select(statisticsFeature.selectChartAndStatsLoading);
  public performance$ = this._store.select(statisticsFeature.selectPerformance);
  public dividends$ = this._store.select(statisticsFeature.selectDividends);
  public dividendYields$ = this._store.select(StatisticsSelectors.selectDividendYields);
  public dividendYieldTotalByPosition$ = this._store.select(statisticsFeature.selectDividendYieldTotal);
  public dividendYieldsLoading$ = this._store.select(statisticsFeature.selectDividendYieldsLoading);

  public loadStatistics = (): void => {
    this.loadMyTrades();
    this.loadWeighting(WeightingType.ASSETS);
    this.loadMyNews();
    this.loadTradeTicker();
    this.loadMyChartAndStats();
    this.loadDividends();
  };

  public unsubscribe = (): void => {
    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
    this._store.dispatch(StatisticsActions.unsubscribeFromTradeTicks());
  };

  public loadTradeTicker(): void {
    this._store.dispatch(StatisticsActions.loadTradeTicker());
    this._subscriptions.push(
      this.tradeTickerLoaded$
        .pipe(filter((loaded) => !!loaded))
        .subscribe(() => this._store.dispatch(StatisticsActions.subscribeToTradeTicks())),
    );
  }

  public toggleTradeTickerFilter(tradeTickerFilter: Filter): void {
    this._store.dispatch(StatisticsActions.toggleTradeTickerFilter({ tradeTickerFilter }));
  }

  public loadWeighting(type: WeightingType): void {
    this._store.dispatch(
      type === WeightingType.ASSETS ? StatisticsActions.loadWeightingByAssetType() : StatisticsActions.loadWeightingByTopPositions(),
    );
  }

  public loadWeightingByPositions(): void {
    this._store.dispatch(StatisticsActions.loadWeightingByPositions());
  }

  public loadWeightingByTopPositions(): void {
    this._store.dispatch(StatisticsActions.loadWeightingByTopPositions());
  }

  public loadMyNews(): void {
    this._store.dispatch(StatisticsActions.loadMyNews());
  }

  public loadMyTrades(): void {
    this._store.dispatch(StatisticsActions.loadMyTrades());
  }

  public loadDividends(): void {
    this._store.dispatch(StatisticsActions.loadDividends());
  }

  public loadMyChartAndStats(force = false): void {
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this._store.dispatch(StatisticsActions.loadChartAndStats({ force }));
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this._store.dispatch(StatisticsActions.subscribeToPerformance());
  }

  public changeChartPeriod(period: Period | number): void {
    let from: Date, to: Date;

    switch (period) {
      case Period.MAX:
        break;
      case Period.ONE_YEAR:
        from = new Date();
        from.setFullYear(new Date().getFullYear() - 1);
        from.setHours(0, 0, 0, 0);
        to = new Date();
        to.setHours(23, 59, 59, 999);
        break;
      case Period.THREE_MONTHS:
        from = new Date();
        from.setMonth(new Date().getMonth() - 3);
        from.setHours(0, 0, 0, 0);
        to = new Date();
        to.setHours(23, 59, 59, 999);
        break;
      default:
        from = new Date(period, 0, 1, 0, 0, 0, 0);
        to = new Date(period, 11, 31, 23, 59, 59, 999);
        break;
    }

    this._store.dispatch(StatisticsActions.changeChartPeriod({ from, to }));
  }

  public loadDividendYield(force?: boolean): void {
    this._store.dispatch(StatisticsActions.loadDividendYields({ force }));
  }

  public loadDividendYieldTotalByPosition(isin: string): void {
    this._store.dispatch(StatisticsActions.loadDividendYieldTotalByPosition({ isin }));
  }

  public clearDividendYieldTotalByPosition(): void {
    this._store.dispatch(StatisticsActions.clearDividendYieldTotalByPosition());
  }
}
